import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { FaSearch } from "react-icons/fa";
import { HiOutlineEye } from "react-icons/hi2";
import { baseURL } from "../../config";

const api = axios.create({ baseURL });
const requestTypeAliases = {
  restaurant: "Restaurant",
  "e-commerce store": "Restaurant",
  ecommerce: "Restaurant",
  "ecommerce store": "Restaurant",
  taxi: "Taxi",
  deliveryman: "Deliveryman",
  "delivery man": "Deliveryman",
  delivery: "Deliveryman",
};
const requestTypeOptions = ["All", "Restaurant", "Taxi", "Deliveryman"];

const normalizeRequestType = (type = "") => {
  const normalizedKey = type.toString().trim().toLowerCase();
  return requestTypeAliases[normalizedKey] || type;
};

const hasAddress = (request) =>
  normalizeRequestType(request?.type) !== "Deliveryman";

const getRequestDisplayName = (request) =>
  request.businessName || request.customerName || request.name || "N/A";

const Requests = ({
  requestType = "All",
  title = "Service Requests",
  description = "Manage all incoming requests from one table and filter by request type.",
}) => {
  const [requests, setRequests] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedType, setSelectedType] = useState(requestType);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    setSelectedType(requestType);
  }, [requestType]);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setIsLoading(true);
        const response = await api.get("/service-request/fetch");
        setRequests(response?.data?.data || []);
      } catch (error) {
        setRequests([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, []);

  const typeRequests = useMemo(() => {
    if (selectedType === "All") {
      return requests;
    }

    return requests.filter(
      (item) => normalizeRequestType(item.type) === selectedType,
    );
  }, [requests, selectedType]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "TYPE",
        selector: (row) => normalizeRequestType(row.type),
        grow: 0.9,
        cell: (row) => (
          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
            {normalizeRequestType(row.type)}
          </span>
        ),
      },
      {
        name: "NAME",
        selector: (row) => getRequestDisplayName(row),
        grow: 1.15,
      },
      {
        name: "PHONE",
        selector: (row) => row.phone || "N/A",
        grow: 0.9,
      },
    ];

    if (selectedType !== "Deliveryman") {
      baseColumns.push({
        name: "ADDRESS",
        selector: (row) => row.address || "N/A",
        grow: 1.1,
        cell: (row) => (
          <span className="line-clamp-2 whitespace-normal break-words text-sm text-slate-700">
            {row.address || "N/A"}
          </span>
        ),
      });
    }

    baseColumns.push(
      {
        name: "COMMENT",
        selector: (row) => row.comment || "N/A",
        grow: 1.15,
        cell: (row) => (
          <span className="line-clamp-2 whitespace-normal break-words text-sm text-slate-700">
            {row.comment || "N/A"}
          </span>
        ),
      },
      {
        name: "ACTION",
        width: "100px",
        button: true,
      
        cell: (row) => (
          <button
            type="button"
            onClick={() => setSelectedRequest(row)}
            className="-ml-2   inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
          >
            <HiOutlineEye className="text-sm" />
            View
          </button>
        ),
      },
    );

    return baseColumns;
  }, [selectedType]);

  const requestDetailFields = useMemo(() => {
    if (!selectedRequest) {
      return [];
    }

    return [
      {
        label: "Request Type",
        value: normalizeRequestType(selectedRequest.type),
      },
      { label: "Name", value: getRequestDisplayName(selectedRequest) },
      { label: "Phone", value: selectedRequest.phone },
      { label: "Comment", value: selectedRequest.comment, fullWidth: true },
    ].filter(
      (field) => field.label !== "Address" || hasAddress(selectedRequest),
    );
  }, [selectedRequest]);

  const filteredRequests = useMemo(() => {
    const query = filterText.toLowerCase();
    return typeRequests.filter((item) => {
      const searchable = [
        normalizeRequestType(item.type),
        getRequestDisplayName(item),
        item.phone,
        hasAddress(item) ? item.address : "",
        item.comment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [filterText, typeRequests]);

  const customStyles = {
    table: {
      style: {
        width: "100%",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f8fafc",
        color: "#0f172a",
        fontWeight: 700,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        paddingTop: "14px",
        paddingBottom: "14px",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
      },
    },
    cells: {
      style: {
        paddingTop: "8px",
        paddingBottom: "8px",
      },
    },
  };

  const subHeaderComponent = (
    <div className="flex w-full flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
        <select
          value={selectedType}
          onChange={(event) => setSelectedType(event.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500"
        >
          {requestTypeOptions.map((option) => (
            <option key={option} value={option}>
              {option === "All" ? "All Types" : option}
            </option>
          ))}
        </select>
        <div className="relative w-full sm:min-w-[280px]">
          <input
            type="text"
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            placeholder="Search requests"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 outline-none transition focus:border-emerald-500"
          />
          <FaSearch className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-4 py-3">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex min-h-[320px] items-center justify-center text-slate-500">
            Loading requests...
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredRequests}
            pagination
            responsive
            highlightOnHover
            persistTableHead
            customStyles={customStyles}
            subHeader
            subHeaderComponent={subHeaderComponent}
            noDataComponent={
              <div className="px-6 py-10 text-center text-slate-500">
                No requests found.
              </div>
            }
          />
        )}
      </div>

      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4 py-6">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Request details
                </p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">
                  {normalizeRequestType(selectedRequest.type)} Request
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Review the submitted information for this request.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRequest(null)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="grid gap-3 px-6 py-6 sm:grid-cols-2">
              {requestDetailFields.map((field) => (
                <div
                  key={field.label}
                  className={`rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 ${
                    field.fullWidth ? "sm:col-span-2" : ""
                  }`}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    {field.label}
                  </p>
                  <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-800">
                    {field.value || "N/A"}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex justify-end border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setSelectedRequest(null)}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests;
